// Availability Tab — FR-3
// Availability score gauge, status table, at-risk items, supplier risk assessment
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  alpha,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { GaugeChart } from '../../../components/analytics';
import {
  useBomAtRiskItems,
  useBomSupplierRisk,
} from '../../../hooks/useBomIntelligence';
import { colors } from '../../../theme/analyticsColors';
import { getAvailabilityColor, getConfidenceColor, getScoreColor } from '../utils/colors';
import type { BomAvailabilityResponse, AvailabilityLineItem } from '../../../types';

interface AvailabilityTabProps {
  bomId: string;
  availabilityData: BomAvailabilityResponse;
  onViewPart?: (partId: string) => void;
}

const stockStatusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  IN_STOCK: { label: 'In Stock', color: colors.success, icon: <CheckIcon sx={{ fontSize: 14 }} /> },
  AVAILABLE: { label: 'Available', color: colors.chart.primary, icon: <CheckIcon sx={{ fontSize: 14 }} /> },
  LOW_STOCK: { label: 'Low Stock', color: colors.warning, icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  ON_ORDER: { label: 'On Order', color: colors.warning, icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
  OUT_OF_STOCK: { label: 'Out of Stock', color: colors.error, icon: <CancelIcon sx={{ fontSize: 14 }} /> },
  DISCONTINUED: { label: 'Discontinued', color: colors.error, icon: <CancelIcon sx={{ fontSize: 14 }} /> },
  UNKNOWN: { label: 'Unknown', color: colors.secondary, icon: <UnknownIcon sx={{ fontSize: 14 }} /> },
};

const riskLevelConfig: Record<string, { color: string; weight: number }> = {
  LOW: { color: colors.success, weight: 400 },
  MEDIUM: { color: colors.warning, weight: 500 },
  HIGH: { color: colors.error, weight: 600 },
  CRITICAL: { color: colors.error, weight: 700 },
};

const AvailabilityTab: React.FC<AvailabilityTabProps> = ({ bomId, availabilityData, onViewPart }) => {
  const rollup = availabilityData.rollup;
  const lineItems = availabilityData.lineItemAvailability || [];
  const { data: atRiskItems, isLoading: atRiskLoading } = useBomAtRiskItems(bomId);
  const { data: supplierRisks, isLoading: supplierRiskLoading } = useBomSupplierRisk(bomId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Availability Score + Key Metrics */}
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            {/* Gauge */}
            <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <GaugeChart
                value={rollup?.availabilityScore ?? 0}
                thresholds={[
                  { value: 50, color: colors.error, label: 'Critical' },
                  { value: 75, color: colors.warning, label: 'At Risk' },
                  { value: 100, color: colors.success, label: 'Healthy' },
                ]}
                label="Availability Score"
                size="medium"
              />
            </Grid>

            {/* Key Metrics */}
            <Grid size={{ xs: 12, md: 9 }}>
              <Grid container spacing={2}>
                {[
                  { label: 'In Stock', value: rollup?.inStockCount ?? 0, total: rollup?.totalLineItems, color: colors.success },
                  { label: 'At Risk', value: rollup?.atRiskCount ?? 0, total: rollup?.totalLineItems, color: colors.warning },
                  { label: 'Unavailable', value: rollup?.unavailableCount ?? 0, total: rollup?.totalLineItems, color: colors.error },
                  { label: 'Single Source', value: rollup?.singleSourceCount ?? 0, total: rollup?.totalLineItems, color: colors.chart.purple },
                  { label: 'Longest Lead Time', value: `${rollup?.longestLeadTimeDays ?? 0}d`, color: colors.primary },
                  { label: 'Avg Lead Time', value: `${rollup?.averageLeadTimeDays ?? 0}d`, color: colors.primary },
                ].map((metric) => (
                  <Grid key={metric.label} size={{ xs: 6, sm: 4, md: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: metric.color, lineHeight: 1 }}>
                        {metric.value}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6875rem', color: colors.secondary, mt: 0.5, textTransform: 'uppercase', fontWeight: 500 }}>
                        {metric.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Availability Status Table */}
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.border}` }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.primary }}>
              Line Item Availability
            </Typography>
            <Typography variant="body2" sx={{ color: colors.secondary, mt: 0.5 }}>
              {availabilityData.enrichedCount} of {availabilityData.lineItemCount} items enriched
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 450 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Part Number', 'Description', 'Qty', 'Stock Status', 'Lead Time', 'Risk', 'Suppliers', 'Alternatives', 'Confidence'].map(header => (
                    <TableCell
                      key={header}
                      align={['Qty', 'Lead Time', 'Suppliers', 'Alternatives'].includes(header) ? 'right' : 'left'}
                      sx={{ bgcolor: colors.surfaceAlt, fontWeight: 600, color: colors.primary, fontSize: '0.75rem', textTransform: 'uppercase' }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item: AvailabilityLineItem, idx: number) => {
                  const ai = item.availabilityIntelligence;
                  const stockCfg = stockStatusConfig[ai?.stockStatus || 'UNKNOWN'] || stockStatusConfig.UNKNOWN;
                  const riskCfg = riskLevelConfig[ai?.riskLevel || 'LOW'] || riskLevelConfig.LOW;

                  return (
                    <TableRow
                      key={item.lineItemId}
                      hover
                      sx={{ bgcolor: idx % 2 === 0 ? 'transparent' : alpha(colors.surfaceAlt, 0.5) }}
                    >
                      <TableCell sx={{ color: colors.secondary, fontSize: '0.8125rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {item.isCritical && <WarningIcon sx={{ fontSize: 14, color: colors.error }} />}
                          {item.lineNumber}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: onViewPart && item.partId ? colors.chart.primary : colors.primary,
                          ...(onViewPart && item.partId && {
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }),
                        }}
                        onClick={() => onViewPart && item.partId && onViewPart(item.partId)}
                      >
                        {item.partNumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', color: colors.primary, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.partDescription || '\u2014'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', color: colors.primary }}>{item.quantity}</TableCell>
                      <TableCell>
                        {ai ? (
                          <Chip
                            icon={stockCfg.icon as React.ReactElement}
                            label={stockCfg.label}
                            size="small"
                            sx={{
                              bgcolor: alpha(stockCfg.color, 0.08),
                              color: stockCfg.color,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              '& .MuiChip-icon': { color: stockCfg.color },
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.75rem', color: colors.light }}>{'\u2014'}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', color: colors.primary }}>
                        {ai?.leadTimeDays != null ? `${ai.leadTimeDays}d` : '\u2014'}
                      </TableCell>
                      <TableCell>
                        {ai ? (
                          <Chip
                            label={ai.riskLevel}
                            size="small"
                            sx={{
                              bgcolor: alpha(riskCfg.color, 0.08),
                              color: riskCfg.color,
                              fontWeight: riskCfg.weight,
                              fontSize: '0.6875rem',
                            }}
                          />
                        ) : '\u2014'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', color: colors.primary }}>
                        {ai?.supplierCount ?? '\u2014'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', color: colors.primary }}>
                        {ai?.alternativeCount ?? '\u2014'}
                      </TableCell>
                      <TableCell>
                        {ai ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={ai.confidence * 100}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: colors.surfaceAlt,
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: getScoreColor(ai.confidence * 100),
                                  borderRadius: 3,
                                },
                              }}
                            />
                            <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, minWidth: 32 }}>
                              {Math.round(ai.confidence * 100)}%
                            </Typography>
                          </Box>
                        ) : '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Bottom Row: At-Risk Items + Supplier Risk */}
      <Grid container spacing={3}>
        {/* At-Risk Items */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningIcon sx={{ color: colors.error, fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.primary }}>
                  At-Risk Items
                </Typography>
                {atRiskItems && atRiskItems.length > 0 && (
                  <Chip
                    label={atRiskItems.length}
                    size="small"
                    sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error, fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
              </Box>
              {atRiskLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[1, 2, 3].map(i => <Skeleton key={i} height={56} />)}
                </Box>
              ) : atRiskItems && atRiskItems.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {atRiskItems.slice(0, 8).map((item) => {
                    const riskCfg = riskLevelConfig[item.riskLevel] || riskLevelConfig.LOW;
                    const stockCfg = stockStatusConfig[item.stockStatus] || stockStatusConfig.UNKNOWN;
                    return (
                      <Box
                        key={item.lineItemId}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(riskCfg.color, 0.03),
                          border: '1px solid',
                          borderColor: alpha(riskCfg.color, 0.12),
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.isCritical && <WarningIcon sx={{ fontSize: 14, color: colors.error }} />}
                            <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8125rem', fontWeight: 500, color: colors.primary }}>
                              {item.partNumber}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip label={item.riskLevel} size="small" sx={{ bgcolor: alpha(riskCfg.color, 0.1), color: riskCfg.color, fontWeight: 600, fontSize: '0.6875rem' }} />
                            <Chip label={stockCfg.label} size="small" sx={{ bgcolor: alpha(stockCfg.color, 0.1), color: stockCfg.color, fontSize: '0.6875rem' }} />
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, mt: 0.5 }}>
                          {item.partDescription || 'No description'} · {item.supplierCount} supplier{item.supplierCount !== 1 ? 's' : ''} · {item.alternativeCount} alternative{item.alternativeCount !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CheckIcon sx={{ fontSize: 32, color: colors.success, mb: 1 }} />
                  <Typography sx={{ color: colors.secondary }}>No at-risk items detected</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Supplier Risk Assessment */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.border}` }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.primary }}>
                  Supplier Risk Assessment
                </Typography>
              </Box>
              {supplierRiskLoading ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3].map(i => <Skeleton key={i} height={40} sx={{ mb: 1 }} />)}
                </Box>
              ) : supplierRisks && supplierRisks.length > 0 ? (
                <TableContainer sx={{ maxHeight: 350 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Supplier', 'Parts', 'Delivery', 'Quality', 'Composite'].map(h => (
                          <TableCell key={h} sx={{ bgcolor: colors.surfaceAlt, fontWeight: 600, color: colors.primary, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplierRisks.map((sr) => (
                        <TableRow key={sr.supplierId} hover>
                          <TableCell sx={{ fontSize: '0.8125rem', color: colors.primary, fontWeight: 500 }}>
                            {sr.supplierName || sr.supplierId}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', color: colors.primary }}>
                            {sr.partsSupplied}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={sr.riskScore.deliveryReliability}
                                sx={{
                                  width: 48,
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: colors.surfaceAlt,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: getScoreColor(sr.riskScore.deliveryReliability),
                                    borderRadius: 3,
                                  },
                                }}
                              />
                              <Typography sx={{ fontSize: '0.6875rem', color: colors.secondary }}>
                                {Math.round(sr.riskScore.deliveryReliability)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={sr.riskScore.qualityScore}
                                sx={{
                                  width: 48,
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: colors.surfaceAlt,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: getScoreColor(sr.riskScore.qualityScore),
                                    borderRadius: 3,
                                  },
                                }}
                              />
                              <Typography sx={{ fontSize: '0.6875rem', color: colors.secondary }}>
                                {Math.round(sr.riskScore.qualityScore)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={Math.round(sr.riskScore.compositeScore)}
                              size="small"
                              sx={{
                                bgcolor: alpha(getScoreColor(sr.riskScore.compositeScore), 0.1),
                                color: getScoreColor(sr.riskScore.compositeScore),
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                minWidth: 40,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
                  <Typography sx={{ color: colors.light }}>No supplier risk data. Run availability enrichment first.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AvailabilityTab;
